import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { DataPanel, MonoLabel, StatusBadge } from "@/components/primitives";
import { useSEO } from "@/lib/useSEO";
import { PageShell } from "@/components/PageShell";

const FIELDS = [
  ["ACTOR", "Who is on the hook for the outcome."],
  ["OBJECTIVE", "What the actor is trying to achieve."],
  ["BASELINE", "The measured starting state."],
  ["TARGET", "The committed, measurable result."],
  ["MEASUREMENT", "How the delta is computed."],
  ["DEADLINE", "When the measurement window closes."],
  ["EVIDENCE SOURCE", "Where proof is collected from."],
  ["VERIFICATION METHOD", "On-chain, oracle, or committee."],
  ["STAKE", "Collateral posted against the claim."],
  ["REWARD", "What verified success returns."],
  ["CONSEQUENCE", "What happens to the actor either way."],
] as const;

const LIFECYCLE = ["DRAFT", "STAKED", "ACTIVE", "MEASURED", "VERIFIED", "SETTLED"];

export default function HowItWorks() {
  useSEO({
    title: "How It Works — Outcome Contracts | Consequence Protocol",
    description:
      "Outcome Contracts bind actors to measurable, time-boxed results — turning intent into falsifiable, verifiable commitment.",
    path: "/how-it-works",
  });

  return (
    <PageShell testId="page-how-it-works">
      <section className="container-t pb-16 pt-20 sm:pt-28">
        <SectionHeader
          index="00"
          label="How It Works"
          title={
            <>
              Outcome Contracts: intent, <span className="text-cyan">made falsifiable</span>
            </>
          }
          description="An Outcome Contract defines the terms under which an action can be judged. Because every field is explicit, verification is deterministic — and reputation is earned on evidence, not narrative."
        />
      </section>

      <section className="container-t pb-24" data-testid="section-contract-anatomy">
        <div className="grid items-start gap-8 lg:grid-cols-2">
          <Reveal>
            <DataPanel title="Contract Anatomy" meta="11 FIELDS">
              <div>
                {FIELDS.map(([k, v]) => (
                  <div key={k} className="flex flex-col gap-1 border-b border-line/60 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                    <MonoLabel className="text-cyan">{k}</MonoLabel>
                    <span className="text-sm text-dim sm:text-right sm:max-w-[60%]">{v}</span>
                  </div>
                ))}
              </div>
            </DataPanel>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="relative">
              <div className="absolute -top-3 right-4 z-10 border border-warn/40 bg-warn/10 px-2 py-1">
                <MonoLabel className="text-[8px] text-warn">Protocol concept — not deployed</MonoLabel>
              </div>
              <DataPanel title="Outcome Contract #0471" meta="SPECIMEN" scan>
                <div className="flex items-center justify-between">
                  <span className="font-display text-xl font-bold text-ink">Liquidity Growth Contract</span>
                  <StatusBadge status="ACTIVE" />
                </div>
                <div className="mt-6 space-y-0">
                  {[
                    ["OBJECTIVE", "Increase liquidity"],
                    ["BASELINE", "$1,000,000"],
                    ["TARGET", "$1,250,000"],
                    ["DEADLINE", "30 DAYS"],
                    ["STAKE", "500 USDC"],
                    ["VERIFICATION", "ON-CHAIN"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between border-b border-line/60 py-3">
                      <MonoLabel className="text-[9px]">{k}</MonoLabel>
                      <span className="font-mono text-sm text-ink">{v}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <div className="flex justify-between">
                    <MonoLabel className="text-[9px]">Time elapsed</MonoLabel>
                    <MonoLabel className="text-[9px] text-cyan">12 / 30 days</MonoLabel>
                  </div>
                  <div className="mt-2 h-1 w-full bg-navy">
                    <div className="h-full w-[40%] bg-gradient-to-r from-blue to-cyan" />
                  </div>
                </div>
                <div className="mt-6 border-t border-line pt-4">
                  <MonoLabel className="text-[8px] text-dim/60">Specimen interface — all values illustrative</MonoLabel>
                </div>
              </DataPanel>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-line bg-panel/40 py-24" data-testid="section-lifecycle">
        <div className="container-t">
          <SectionHeader index="01" label="Lifecycle" title="From draft to settled consequence" />
          <Reveal>
            <div className="mt-12 flex flex-wrap items-center gap-3">
              {LIFECYCLE.map((s, i) => (
                <span key={s} className="flex items-center gap-3">
                  <span className="border border-line bg-base px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-colors duration-200 hover:border-electric/50 hover:text-cyan">
                    {s}
                  </span>
                  {i < LIFECYCLE.length - 1 && <span className="font-mono text-cyan/60">→</span>}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {[
                ["DISPUTE WINDOW", "Ambiguous evidence opens a bonded dispute before settlement. Disputed outcomes are flagged in the registry until resolved."],
                ["MISSED CLAIMS", "Failure to reach target is settled honestly and recorded. Track records without failures are reputations without information."],
                ["SETTLEMENT", "Verdicts are append-only. Stakes and rewards resolve according to the contract’s consequence terms."],
              ].map(([t, d]) => (
                <DataPanel key={t} title={t} meta="CONCEPT">
                  <p className="text-sm leading-relaxed text-dim">{d}</p>
                </DataPanel>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
